import React, { useMemo, useState } from 'react';
import Title from '../../Title';
import {
    Alert,
    Card,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import useTranslate from '../../../../../customHooks/useTranslate';
import {
    useNoteDetail,
    useProperty,
} from '../../../../contextsProperty/ContextProperties';
import DotIcon from '../../../../components/DotIcon';
import Menu from '../../../components/Menu';
import useLanguage from '../../../../../customHooks/useLanguage';
import {
    categorizePeriod,
    formatDate,
    isMinValueDate,
} from '../../../../../utils/formatDates';
import DeletePopUp from '../../DeletePopUp';
import NewNoteDetail from './NewNoteDetail';
import Tag from '../../../../../muiComponents/Tag';
import {
    NOTE_DATE_TAG_COLOR,
    NOTE_DETAIL_INITIAL_STATE,
    NOTE_TYPE_DATE_TAG_COLOR,
} from '../../../../../utils/property/propertyUtils';
import SearchBar from '../../../../components/SearchBar';
import Button from '../../../../../muiComponents/Button';
import Tooltip from '../../../../../muiComponents/Tooltip';

const SpecialNotes = () => {
    const translate = useTranslate();
    const { language } = useLanguage();
    const { notes, modidyCollectionItem } = useProperty();
    const { handleNoteDetailChange } = useNoteDetail();
    const [idDeletePopUpVisible, setIdDeletePopUpVisible] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const visibleNotes = useMemo(() => {
        return notes.filter(
            note =>
                !note.Deleted &&
                note.message.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [notes, searchTerm]);

    const isDateless = (fromDate: Date | null, toDate: Date | null) => {
        return (
            (!fromDate || isMinValueDate(fromDate)) &&
            (!toDate || isMinValueDate(toDate))
        );
    };

    const getNoteDateTitle = (fromDate: Date | null, toDate: Date | null) => {
        const hasFrom = fromDate && !isMinValueDate(fromDate);
        const hasTo = toDate && !isMinValueDate(toDate);

        if (!hasFrom && !hasTo) return translate('dateless');

        if (hasFrom && !hasTo)
            return `${translate('from')} ${formatDate(fromDate, language)}`;

        if (!hasFrom && hasTo)
            return `${translate('until')} ${formatDate(toDate, language)}`;

        return `${formatDate(fromDate, language)} - ${formatDate(
            toDate,
            language
        )}`;
    };

    const addNewNote = () => {
        handleNoteDetailChange({
            ...NOTE_DETAIL_INITIAL_STATE,
            isOpenPanel: true,
            enabled: true,
        });
    };

    return (
        <div className='property-special-notes__content'>
            <div>
                <Title
                    as='h1'
                    text={'special_notes_info_title'}
                    className='text-align-center'
                />
            </div>
            <Card className='padding20'>
                <Alert severity='info'>
                    <p style={{ fontWeight: 'normal' }}>
                        {translate('special_notes_info_subtitle')}
                    </p>
                </Alert>
            </Card>
            <div className='flex flex-align-center gap-20 '>
                <SearchBar
                    placeholder={translate('search')}
                    onChange={e => {
                        setSearchTerm(e.target.value);
                    }}
                    value={searchTerm}
                    onClear={() => {
                        setSearchTerm('');
                    }}
                />
                <Button
                    variant='contained'
                    onClick={() => {
                        addNewNote();
                    }}
                    sx={{ whiteSpace: 'nowrap', height: 32 }}
                >
                    {translate('add_note')}
                </Button>
            </div>
            <Card className='flex flex-center overflow-y-auto'>
                {visibleNotes.length ? (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    style={{ textTransform: 'capitalize' }}
                                >
                                    {translate('status')}
                                </TableCell>
                                <TableCell>{translate('note')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleNotes.map(note => {
                                const datelessNote = isDateless(
                                    note.fromDate,
                                    note.toDate
                                );
                                const noteTitle = getNoteDateTitle(
                                    note.fromDate,
                                    note.toDate
                                );
                                const tagText = categorizePeriod(
                                    note.fromDate,
                                    note.toDate
                                );

                                return (
                                    <TableRow key={note.id}>
                                        <TableCell
                                            sx={{
                                                verticalAlign: 'text-top',
                                                width: 40,
                                            }}
                                        >
                                            <Tooltip
                                                titleText={
                                                    note.enabled
                                                        ? 'enabled'
                                                        : 'disabled'
                                                }
                                            >
                                                <span>
                                                    <DotIcon
                                                        truthy={note.enabled}
                                                        falsy={!note.enabled}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                verticalAlign: 'text-top',
                                            }}
                                        >
                                            <div className='flex-column gap-10'>
                                                <div className='flex flex-between '>
                                                    <div className='flex gap-10'>
                                                        <p className='text-capitalize font-weight-600 text-secunday-700'>
                                                            {noteTitle}
                                                        </p>

                                                        {!datelessNote && (
                                                            <Tag
                                                                text={
                                                                    note.fromBookingDate
                                                                        ? translate(
                                                                              'booking_date'
                                                                          )
                                                                        : translate(
                                                                              'check_in_out'
                                                                          )
                                                                }
                                                                color={
                                                                    note.fromBookingDate
                                                                        ? NOTE_TYPE_DATE_TAG_COLOR.BOOKING_DATE
                                                                        : NOTE_TYPE_DATE_TAG_COLOR.CHECK_IN_DATE
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                    <Tag
                                                        text={tagText}
                                                        color={
                                                            NOTE_DATE_TAG_COLOR[
                                                                tagText
                                                            ]
                                                        }
                                                        className='text-capitalize'
                                                    />
                                                </div>
                                                {note.message}
                                            </div>
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                verticalAlign: 'text-top',
                                                width: 40,
                                            }}
                                        >
                                            <Menu
                                                menuItems={[
                                                    {
                                                        text: 'edit',
                                                        icon: 'Edit',
                                                        onClick: () => {
                                                            handleNoteDetailChange(
                                                                {
                                                                    ...note,
                                                                    isOpenPanel:
                                                                        true,
                                                                }
                                                            );
                                                        },
                                                    },
                                                    {
                                                        text: note.enabled
                                                            ? 'disable'
                                                            : 'enable',
                                                        icon: note.enabled
                                                            ? 'DoDisturb'
                                                            : 'FiberManualRecord',
                                                        onClick: () => {
                                                            modidyCollectionItem(
                                                                'notes',
                                                                'update',
                                                                {
                                                                    ...note,
                                                                    enabled:
                                                                        !note.enabled,
                                                                }
                                                            );
                                                        },
                                                    },

                                                    {
                                                        text: 'delete',
                                                        icon: 'Delete',
                                                        onClick: () => {
                                                            setIdDeletePopUpVisible(
                                                                note.id
                                                            );
                                                        },
                                                    },
                                                ]}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <Button
                        variant='text'
                        startIcon={'Add'}
                        onClick={addNewNote}
                    >
                        {translate('add_note')}
                    </Button>
                )}
            </Card>
            <NewNoteDetail />
            {idDeletePopUpVisible && (
                <DeletePopUp
                    open={!!idDeletePopUpVisible}
                    onSave={() => {
                        modidyCollectionItem('notes', 'delete', {
                            id: idDeletePopUpVisible,
                        });
                        setIdDeletePopUpVisible('');
                    }}
                    onCancel={() => {
                        setIdDeletePopUpVisible('');
                    }}
                />
            )}
        </div>
    );
};

export default SpecialNotes;
